/**
 * Mobile note editor mount manager (collaborative mode).
 *
 * The mobile shell re-renders by setting innerHTML on its container,
 * which would destroy any TipTap editor mounted inside. To keep the
 * editor alive (and the user's caret position) across re-renders, we
 * use the same detach/reattach pattern as desktop-preview-mount.js:
 *
 *   - Before re-render: pull the editor's host element out of the
 *     container and stash it on document.body (display:none).
 *   - After re-render: if the new render contains a placeholder
 *     `<div id="mobile-note-content-mount">`, swap our live host
 *     element back in for the placeholder.
 *
 * The TipTap editor is bound to the docNote's Y.XmlFragment via
 * ySyncPlugin, so each keystroke is a CRDT op on the fragment. This
 * eliminates the mobile+desktop concurrent-edit clobber that plain
 * `setDocNoteFromMarkdown` would cause.
 */

// The TipTap stack lives in note-editor-core.js and is dynamic-imported on
// first mount, keeping ProseMirror out of the boot bundle.
let EditorCore = null;
let editorCoreLoading = null;
// Guards the async mount against destroy/re-mount races while the chunk loads.
let mountSeq = 0;

function loadEditorCore() {
  editorCoreLoading ||= import('./note-editor-core.js').then((m) => (EditorCore = m));
  return editorCoreLoading;
}

let editor = null;
let tableToolbar = null;   // floating table controls (delete table/row/column)
let host = null;          // Stable DOM element TipTap mounts into
let mountedNoteId = null; // ID of the note the live editor is bound to
let mountedDocId = null;  // Doc the editor is bound to (for cross-doc tab safety)
let getCallbacks = () => null;
// Captured at detach time: { from, to } — the ProseMirror selection range
// when focus was inside the editor. Restored after reattach so the caret
// doesn't jump to the end of the document on every peer-triggered re-render.
let savedSelection = null;

/**
 * Configure module dependencies. Call once during mobile init.
 * @param {Object} cfg
 * @param {Function} cfg.getCallbacks - returns the mobile callbacks bag
 *   (must expose sharedCallbacks.{isCollabDocNotesEnabled, getDocNoteFragment}).
 */
export function configureMobileNoteEditorMount(cfg) {
  getCallbacks = cfg?.getCallbacks || (() => null);
}

/**
 * Detach the live editor host from the regular render tree before
 * `container.innerHTML = ...` wipes it. Call BEFORE each mobile render.
 *
 * Also captures the current ProseMirror selection if the editor has
 * focus, so we can put the caret back where the user left it after
 * reattach. NOTE: capturing focus has to happen BEFORE we move the
 * element — appendChild to body blurs the contenteditable and
 * `editor.isFocused` would already be false by then.
 */
/**
 * The live collab editor, or null when the note is unmigrated (textarea mode)
 * or nothing is being edited. Used by the attachment uploader to insert an
 * `attachment:` reference into the note the user is actually editing.
 */
export function getMobileNoteEditor() {
  return editor;
}

export function detachMobileNoteEditor() {
  if (!host) return;
  try {
    if (editor && !editor.isDestroyed && editor.isFocused) {
      const sel = editor.state?.selection;
      if (sel) {
        savedSelection = { from: sel.from, to: sel.to };
      }
    }
  } catch (err) {
    // Selection capture is best-effort — never block the detach on it.
    savedSelection = null;
  }
  if (host.parentElement && host.parentElement !== document.body) {
    host.style.display = 'none';
    document.body.appendChild(host);
  }
}

/**
 * Re-attach (or fresh-mount) the editor after a mobile render.
 * - If a placeholder div is present in the new DOM AND we have a live
 *   editor bound to the right note, swap it in.
 * - If a placeholder is present AND no live editor exists, mount a
 *   new one.
 * - If no placeholder is present (user navigated away from the editor),
 *   destroy any live editor we still have.
 *
 * Call AFTER each mobile render's post-render hook.
 *
 * @param {Object} ctx
 * @param {string} ctx.noteId - The currently-edited note id (or null).
 * @param {string} ctx.docId  - The doc the note belongs to (or null).
 */
export function syncMobileNoteEditorMount(ctx) {
  const placeholder = document.getElementById('mobile-note-content-mount');
  const desiredNoteId = ctx?.noteId || null;
  const desiredDocId = ctx?.docId || null;

  // No placeholder → user is no longer editing in collab mode → tear down.
  if (!placeholder) {
    if (editor || host) destroyMobileNoteEditor();
    return;
  }

  // Editor exists but for a different note/doc — destroy and re-mount.
  if (editor && (mountedNoteId !== desiredNoteId || mountedDocId !== desiredDocId)) {
    destroyMobileNoteEditor();
  }

  // Already mounted for the right note → just swap host into placeholder.
  if (editor && host) {
    if (host !== placeholder && placeholder.parentElement) {
      placeholder.parentElement.replaceChild(host, placeholder);
      host.style.display = '';
    }
    // Best-effort focus + caret restoration. Programmatic focus outside a
    // user-gesture stack won't always pop the mobile keyboard back up —
    // that's a browser constraint we can't work around here — but the
    // ProseMirror selection IS restored so the caret stays put when the
    // user re-taps to resume typing. scrollIntoView is suppressed so the
    // keyboard appearing later doesn't yank the viewport.
    if (savedSelection) {
      const sel = savedSelection;
      savedSelection = null;
      requestAnimationFrame(() => {
        if (!editor || editor.isDestroyed) return;
        try {
          editor.chain().focus(null, { scrollIntoView: false }).setTextSelection(sel).run();
        } catch (err) {
          // Selection out of range (e.g., doc shrunk via peer edit) — fall
          // back to a plain focus at the document end.
          try { editor.commands.focus('end', { scrollIntoView: false }); } catch {}
        }
      });
    }
    return;
  }

  // No editor yet — mount a fresh one if we have everything we need.
  if (!desiredNoteId) return;
  const cbs = getCallbacks();
  const sc = cbs?.sharedCallbacks;
  if (!sc?.isCollabDocNotesEnabled?.(desiredDocId)) return;
  const fragment = sc.getDocNoteFragment?.(desiredNoteId, desiredDocId);
  if (!fragment) return;

  // The editor chunk loads async on the very first mount (cached after).
  // The placeholder stays in the DOM while it loads; the seq guard drops
  // this mount if the user navigates away or a newer mount supersedes it.
  const seq = ++mountSeq;
  loadEditorCore().then(() => {
    if (seq !== mountSeq) return; // destroyed or superseded while loading
    if (editor) return; // a later sync already mounted for this note
    const ph = document.getElementById('mobile-note-content-mount');
    if (!ph) return; // user left edit mode while the chunk loaded

    host = document.createElement('div');
    host.className = 'mobile-note-editor-host';
    // Replace the placeholder with our live host element.
    if (ph.parentElement) {
      ph.parentElement.replaceChild(host, ph);
    }

    const made = EditorCore.createMobileNoteEditor({
      host,
      fragment,
      getCurrentDocId: () => mountedDocId,
    });
    editor = made.editor;
    tableToolbar = made.tableToolbar;
    mountedNoteId = desiredNoteId;
    mountedDocId = desiredDocId;
  });
}

/**
 * Tear down the live editor and remove the host element. Called on
 * exit-edit-mode and on note/doc switches.
 */
export function destroyMobileNoteEditor() {
  ++mountSeq; // cancel any in-flight lazy mount
  try {
    // Before the editor: the toolbar unsubscribes from selectionUpdate on
    // teardown, which needs the editor still alive.
    if (tableToolbar) { tableToolbar.destroy(); tableToolbar = null; }
  } catch { /* already gone */ }
  try {
    if (editor && !editor.isDestroyed) editor.destroy();
  } catch (err) {
    console.warn('[MobileNoteEditor] destroy threw:', err);
  }
  editor = null;
  if (host) {
    try { host.remove(); } catch {}
  }
  host = null;
  mountedNoteId = null;
  mountedDocId = null;
  savedSelection = null;
}

